import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { AdminSectionCard } from "@/components/admin/AdminSectionCard";
import { TrialPolicyCard } from "@/components/billing/TrialPolicyCard";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
/**
 * The standalone `toast`, not `useToast().toast`.
 *
 * They are the same function, but `useToast()` also subscribes the caller to
 * the toast store, and `dispatch` (`use-toast.ts:117-122`) notifies with
 * `listeners.forEach` while each listener's `setState` can synchronously
 * re-render a component whose effect re-registers itself at the end of that
 * same array (`:163-172`, dependency `[state]`). With two subscribers on one
 * screen the iteration can skip `Toaster`'s listener and the toast never
 * paints. This page raises toasts and never reads them, so it does not
 * subscribe — and `use-toast.ts` stays untouched.
 */
import { toast } from "@/hooks/use-toast";
import { Badge } from "@/components/ui/badge";
import { ToastAction } from "@/components/ui/toast";
import { StripeSyncBadge } from "@/components/billing/StripeSyncBadge";
import { SyncPlanDialog } from "@/components/billing/SyncPlanDialog";
import { describeApiFailure, failureLead, failureReference } from "@/lib/api-error";
import type { ApiFailure } from "@/lib/api-error";
import { PLAN_FEATURE_KEYS, entitlementFormSchema } from "@/lib/schemas/plan-management-schemas";
import {
  listPlans,
  setPlanActiveState,
  syncPlanToStripe,
  updatePlanEntitlements,
} from "@/services/plan-management";
import type { PlanEntitlement, SubscriptionPlanSummary } from "@/services/types";

type EditableEntitlement = Pick<PlanEntitlement, "featureKey" | "enabled" | "limit">;

/**
 * Per-row sync state, and it exists because neither the toast nor the mutation
 * can hold it.
 *
 * `use-toast.ts:4` sets `TOAST_LIMIT = 1`, so four failures produce one visible
 * toast — the last — and it is gone in five seconds. And one `useMutation`
 * instance is shared by every row, so `isPending` describes whichever row is in
 * flight and `error` holds only the most recent rejection: even at a limit of
 * four the row state would still be missing. The toast is what makes a failure
 * noticed; this is what makes it legible.
 *
 * It is not durable. After a reload the badge still reads `Failed` — that is a
 * real column — but the sentence is gone, because no column holds one.
 */
type SyncPhase = "running" | "synced" | "failed";

interface PlanSyncState {
  phase: SyncPhase;
  /** Only ever set on `failed`. */
  reason?: { head: string; tail: string };
}

const PHASE_LABEL: Record<SyncPhase, string> = {
  running: "Syncing…",
  synced: "Synced just now",
  failed: "Failed",
};

const PHASE_VARIANT: Record<SyncPhase, "secondary" | "success" | "destructive"> = {
  running: "secondary",
  synced: "success",
  failed: "destructive",
};

/**
 * M10's row sentence. It names the row state and the next action rather than a
 * cause, because on production the cause never crosses the wire:
 * `api/src/app/index.ts:428-430` replaces a non-`HTTPException` message with
 * `Internal server error`, and a non-subscription 403 arrives as a plain
 * `Error` with no status at all.
 */
function syncFailureReason(failure: ApiFailure): { head: string; tail: string } {
  return {
    head:
      failure.status === null
        ? "Stripe sync failed."
        : `Stripe sync failed — HTTP ${failure.status}.`,
    tail: `${failure.message === null ? "" : `“${failure.message}” `}The catalog row is marked failed. Retry, or check the Worker log.`,
  };
}

function FailureToastBody({ failure, tail }: { failure: ApiFailure; tail: string }) {
  return (
    <>
      <span className="block">
        {failureLead(failure)} {tail}
      </span>
      <span className="mt-1.5 block font-mono text-xs opacity-80">{failureReference(failure)}</span>
    </>
  );
}

function buildEditableEntitlements(plan: SubscriptionPlanSummary): EditableEntitlement[] {
  return PLAN_FEATURE_KEYS.map((featureKey) => {
    const existing = plan.entitlements.find((row) => row.featureKey === featureKey);
    return {
      featureKey,
      enabled: existing?.enabled ?? false,
      limit: existing?.limit ?? null,
    };
  });
}

export default function PlanManagement() {
  const queryClient = useQueryClient();
  const plansQuery = useQuery({
    queryKey: ["subscription-plans"],
    queryFn: listPlans,
  });
  const plans = plansQuery.data?.data ?? [];
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null);
  const [draftEntitlementsByPlanId, setDraftEntitlementsByPlanId] = useState<
    Record<string, EditableEntitlement[]>
  >({});

  const [syncStateByPlanId, setSyncStateByPlanId] = useState<Record<string, PlanSyncState>>({});
  const [dialogPlanId, setDialogPlanId] = useState<string | null>(null);

  const activePlanId = selectedPlanId ?? plans[0]?.id ?? null;
  const selectedPlan = activePlanId
    ? (plans.find((plan) => plan.id === activePlanId) ?? null)
    : null;
  const editableEntitlements = selectedPlan
    ? (draftEntitlementsByPlanId[selectedPlan.id] ?? buildEditableEntitlements(selectedPlan))
    : [];

  const setSyncState = (planId: string, state: PlanSyncState) => {
    setSyncStateByPlanId((current) => ({ ...current, [planId]: state }));
  };

  const syncMutation = useMutation({
    mutationFn: ({ planId }: { planId: string; planName: string }) => syncPlanToStripe(planId),
    onMutate: ({ planId }) => {
      setSyncState(planId, { phase: "running" });
    },
    onSuccess: (_result, { planId, planName }) => {
      setSyncState(planId, { phase: "synced" });
      void queryClient.invalidateQueries({ queryKey: ["subscription-plans"] });
      toast({ title: `${planName} synced to Stripe` });
    },
    // The invalidation is issued first, and it is the half that matters:
    // without it the row keeps rendering whatever the last fetch said while
    // the database says `failed`. The toast is the second half, and it does not
    // wait on the refetch — a notification that arrives only once the network
    // has answered again is not a notification.
    onError: (error, { planId, planName }) => {
      const failure = describeApiFailure(error);
      void queryClient.invalidateQueries({ queryKey: ["subscription-plans"] });
      setSyncState(planId, { phase: "failed", reason: syncFailureReason(failure) });
      toast({
        variant: "destructive",
        title: `Couldn’t sync ${planName} to Stripe`,
        description: (
          <FailureToastBody
            failure={failure}
            tail="The catalog row is marked Failed. Nothing in Stripe was changed by the failed step."
          />
        ),
        action: (
          <ToastAction
            altText={`Retry sync of ${planName}`}
            onClick={() => setDialogPlanId(planId)}
          >
            Retry
          </ToastAction>
        ),
      });
    },
  });

  const activateMutation = useMutation({
    mutationFn: ({ planId, isActive }: { planId: string; planName: string; isActive: boolean }) =>
      setPlanActiveState(planId, isActive),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["subscription-plans"] });
      toast({ title: "Plan activation updated" });
    },
    onError: (error, { planName }) => {
      const failure = describeApiFailure(error);
      void queryClient.invalidateQueries({ queryKey: ["subscription-plans"] });
      toast({
        variant: "destructive",
        title: `Couldn’t change activation for ${planName}`,
        description: (
          <FailureToastBody
            failure={failure}
            tail="The list has been refreshed — check the row before trying again."
          />
        ),
      });
    },
  });

  const entitlementsMutation = useMutation({
    mutationFn: ({
      planId,
      entitlements,
    }: {
      planId: string;
      planName: string;
      entitlements: EditableEntitlement[];
    }) => updatePlanEntitlements(planId, entitlements),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["subscription-plans"] });
      toast({ title: "Entitlements saved" });
    },
    onError: (error, { planName }) => {
      const failure = describeApiFailure(error);
      void queryClient.invalidateQueries({ queryKey: ["subscription-plans"] });
      toast({
        variant: "destructive",
        title: `Couldn’t save entitlements for ${planName}`,
        description: (
          <FailureToastBody
            failure={failure}
            tail="The list has been refreshed — check the row before trying again."
          />
        ),
      });
    },
  });

  const selectedPlanActive = selectedPlan?.isActive ?? false;

  return (
    <div className="space-y-6">
      <AdminPageHeader
        title="Plan Management"
        description="Manage catalog plans, entitlements, and Stripe sync state."
      />

      {/* Above the Plans table deliberately. The trial length is policy for every future
          account; the table below is the Stripe-synced catalogue. Putting the policy first is
          the ordering that says which one is which — and the card carries no pill, badge or
          status dot precisely so it cannot be mistaken for a catalogue row. */}
      <TrialPolicyCard />

      <AdminSectionCard
        title="Plans"
        description="Select a plan to edit entitlements, sync Stripe pricing, or toggle activation."
      >
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Tier</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Monthly</TableHead>
              <TableHead>Annual</TableHead>
              <TableHead>Discount</TableHead>
              <TableHead>Sync</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {plans.map((plan) => {
              const isSelected = plan.id === selectedPlanId;
              const syncState = syncStateByPlanId[plan.id];
              const hasFailed = syncState?.phase === "failed";
              return (
                <TableRow
                  key={plan.id}
                  className={isSelected ? "bg-muted/50" : "cursor-pointer"}
                  onClick={() => setSelectedPlanId(plan.id)}
                >
                  <TableCell>{plan.tier}</TableCell>
                  <TableCell className="font-medium">{plan.name}</TableCell>
                  <TableCell>${(plan.monthlyPrice / 100).toFixed(2)}</TableCell>
                  <TableCell>${(plan.annualPrice / 100).toFixed(2)}</TableCell>
                  <TableCell>{plan.annualDiscountPercent}%</TableCell>
                  <TableCell>
                    {syncState ? (
                      <>
                        <Badge variant={PHASE_VARIANT[syncState.phase]}>
                          {PHASE_LABEL[syncState.phase]}
                        </Badge>
                        {syncState.reason ? (
                          <span className="mt-1 block max-w-[34ch] text-xs text-muted-foreground">
                            <b className="text-destructive">{syncState.reason.head}</b>{" "}
                            {syncState.reason.tail}
                          </span>
                        ) : null}
                      </>
                    ) : (
                      <StripeSyncBadge
                        status={plan.stripeSyncStatus}
                        lastSyncedAt={plan.lastSyncedAt}
                        stripeProductId={plan.stripeProductId}
                      />
                    )}
                  </TableCell>
                  <TableCell>{plan.isActive ? "Active" : "Inactive"}</TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-2">
                      <SyncPlanDialog
                        plan={plan}
                        open={dialogPlanId === plan.id}
                        onOpenChange={(open) => setDialogPlanId(open ? plan.id : null)}
                        onConfirm={() => {
                          setDialogPlanId(null);
                          syncMutation.mutate({ planId: plan.id, planName: plan.name });
                        }}
                      >
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          aria-label={
                            hasFailed
                              ? `Retry sync of ${plan.name} to Stripe`
                              : `Sync ${plan.name} to Stripe`
                          }
                          onClick={(event) => {
                            // The row itself is a selector; opening the
                            // confirmation must not also change the selection.
                            event.stopPropagation();
                          }}
                        >
                          {hasFailed ? "Retry" : "Sync"}
                        </Button>
                      </SyncPlanDialog>
                      <Button
                        type="button"
                        size="sm"
                        variant="secondary"
                        aria-label={
                          plan.isActive ? `Deactivate ${plan.name}` : `Activate ${plan.name}`
                        }
                        onClick={(event) => {
                          event.stopPropagation();
                          activateMutation.mutate({
                            planId: plan.id,
                            planName: plan.name,
                            isActive: !plan.isActive,
                          });
                        }}
                      >
                        {plan.isActive ? "Deactivate" : "Activate"}
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </AdminSectionCard>

      <AdminSectionCard title="Entitlements" description="Edit the selected plan's feature matrix.">
        {selectedPlan ? (
          <form
            className="space-y-4"
            onSubmit={(event) => {
              event.preventDefault();
              const parsed = entitlementFormSchema.safeParse({
                entitlements: editableEntitlements,
              });
              if (!parsed.success) {
                return;
              }
              entitlementsMutation.mutate({
                planId: selectedPlan.id,
                planName: selectedPlan.name,
                entitlements: parsed.data.entitlements,
              });
            }}
          >
            <div className="space-y-3">
              {editableEntitlements.map((row, index) => (
                <div
                  key={row.featureKey}
                  className="grid grid-cols-[minmax(0,1fr)_auto_10rem] items-center gap-4 rounded-2xl border border-border/70 px-4 py-3"
                >
                  <div className="space-y-1">
                    <div className="font-medium">{row.featureKey}</div>
                    <div className="text-xs text-muted-foreground">
                      {row.enabled ? "Enabled" : "Disabled"}
                    </div>
                  </div>
                  <Checkbox
                    aria-label={row.featureKey}
                    checked={row.enabled}
                    onCheckedChange={(checked) => {
                      setDraftEntitlementsByPlanId((current) => ({
                        ...current,
                        [selectedPlan.id]: editableEntitlements.map((item, itemIndex) =>
                          itemIndex === index ? { ...item, enabled: checked } : item,
                        ),
                      }));
                    }}
                  />
                  <Input
                    type="number"
                    min={0}
                    placeholder="Limit"
                    value={row.limit ?? ""}
                    onChange={(event) => {
                      const value = event.target.value;
                      setDraftEntitlementsByPlanId((current) => ({
                        ...current,
                        [selectedPlan.id]: editableEntitlements.map((item, itemIndex) =>
                          itemIndex === index
                            ? { ...item, limit: value === "" ? null : Number(value) }
                            : item,
                        ),
                      }));
                    }}
                  />
                </div>
              ))}
            </div>
            <div className="flex items-center gap-3">
              <Button type="submit">Save Entitlements</Button>
              <span className="text-sm text-muted-foreground">
                Selected plan: {selectedPlan.name} {selectedPlanActive ? "(active)" : "(inactive)"}
              </span>
            </div>
          </form>
        ) : (
          <div className="rounded-2xl border border-dashed border-border/70 px-4 py-6 text-sm text-muted-foreground">
            Select a plan to edit its entitlement matrix.
          </div>
        )}
      </AdminSectionCard>
    </div>
  );
}
